import React, {useEffect, useState} from "react";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {toast} from "react-toastify";
import {LoaderForm} from "../loader-form/LoaderForm";
import {Box, Container, TextField, Typography} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {IPostJobSettingsInput} from "../../models/inputs/PostJobSettings";
import {POST_JOB_SETTINGS} from "../graphql/mutations/gqlPostJobSettings";
import {GET_JOB_SETTINGS} from "../graphql/queries/gqlGetJobSettings";
import {IJobSettings} from "../../models/JobSettings";
import {GenericModal} from "../generic-modal/GenericModal";
import {JobSettingsEditorButton} from "./JobSettingsEditorButton";
import {Processor} from "../../models/JobDefinitionStep";

interface IJobSettingsFormProps {
	id?: number;
	processor: Processor;
}

export const JobSettingsForm: React.FC<IJobSettingsFormProps> = ({id = 0, processor}) => {
	const [data, setData] = useState<IPostJobSettingsInput>({
		description: "",
		id: id,
		settings: "",
	});
	const [loading, setLoading] = useState(true);
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const [editSettings, setEditSettings] = useState(false);
	
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setData({
			...data,
			[event.target.name]: event.target.value,
		});
	};
	
	const post = async () => {
		await graphQLServices.querytoolClient.mutate({
			mutation: POST_JOB_SETTINGS,
			variables: { input: data },
		});
	};
	
	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		setLoadingSubmit(true);
		post()
			.then(() => {
				toast.success("Success");
			})
			.catch(() => toast.error("Failed 2"))
			.finally(() => setLoadingSubmit(false));
	};
	
	useEffect(() => {
		if (id) {
			graphQLServices.querytoolClient
				.query({
					query: GET_JOB_SETTINGS,
					variables: {
						"filter": {
							"and": {
								"id": {
									"eq": id,
								},
							},
						},
					},
				})
				.then((result) => {
					setData({ ...data, ...result.data.jobSettings.nodes.filter((it: IJobSettings) => it.id === id)[0], __typename: undefined });
				})
				.finally(() => setLoading(false));
		} else {
			setLoading(false);
		}
		// eslint-disable-next-line
	}, []);
	
	if (loading) {
		return <LoaderForm title={"Post a Job Setting"} />;
	}
	
	return (
		<Container maxWidth="sm">
			<Box sx={{ padding: 3 }}>
				<Typography variant="h4" component="h1" align="center" gutterBottom>
					Post a Job Setting
				</Typography>
				<form>
					<TextField
						fullWidth
						label="ID"
						name="id"
						value={data.id}
						onChange={handleChange}
						margin="normal"
						type="number"
						required
						disabled
					/>
					<TextField
						fullWidth
						label="Description"
						name="description"
						value={data.description}
						onChange={handleChange}
						margin="normal"
						required
					/>
					<JobSettingsEditorButton settings={data.settings} setSettings={(settings) => setData({...data, settings: settings})} processor={processor} />
					<Box sx={{ marginTop: 2 }}>
						<LoadingButton loading={loadingSubmit} onClick={handleSubmit} fullWidth variant="contained">
							Post Job Setting
						</LoadingButton>
					</Box>
				</form>
			</Box>
			<GenericModal
				open={editSettings}
				onClose={() => setEditSettings(false)}
				title={"Settings Query"}
			>
				<TextField
					id="outlined-multiline-static"
					label="Settings Query"
					multiline
					rows={20}
					defaultValue={data.settings}
					sx={{minHeight: "500px", height: "100%", width: "100%"}}
					onChange={event => setData({...data, settings: event.currentTarget.value})}
				/>
			</GenericModal>
		</Container>
	);
};
